import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Audio } from '../blocks/Audio/Audio.js';
import { Blockquote } from '../blocks/Blockquote/Blockquote.js';
import { CodeBlock } from '../blocks/CodeBlock/CodeBlock.js';
import { Divider } from '../blocks/Divider/Divider.js';
import { Embed } from '../blocks/Embed/Embed.js';
import { Image } from '../blocks/Image/Image.js';
import { LottieFile } from '../blocks/LottieFile/LottieFile.js';
import { TableOfContents } from '../blocks/TableOfContents/TableOfContents.js';
import { Video } from '../blocks/Video/Video.js';
import { DEFAULT_EDITOR_OPTIONS } from '../constants.js';
import { ContraEditorContextProvider, } from '../context/ContraEditorContext.js';
import { createSchema, } from '../schema.js';
import { camelToDataKebab, inheritedProps } from '@blocknote/core';
import { MantineProvider } from '@mantine/core';
import { useState } from 'react';
const calculateTextLength = (content) => {
    let length = 0;
    for (const item of content) {
        if (item.type === 'text') {
            length += item.text?.length || 0;
        }
        else if (item.type === 'link' && item.content) {
            length += calculateTextLength(item.content);
        }
    }
    return length;
};
// Truncates content blocks to a maximum length while preserving word boundaries
// Returns truncated blocks and whether truncation occurred
const maybeTruncateContent = (inputBlocks, maxLength) => {
    if (!maxLength) {
        return { blocks: inputBlocks, isTruncated: false };
    }
    let totalLength = 0;
    const truncatedBlocks = [];
    let isTruncated = false;
    for (let blockIndex = 0; blockIndex < inputBlocks.length; blockIndex++) {
        const block = inputBlocks[blockIndex];
        const hasNextBlock = blockIndex < inputBlocks.length - 1;
        if (block.content && Array.isArray(block.content)) {
            const blockLength = calculateTextLength(block.content);
            // If block fits entirely, add it and continue
            if (totalLength + blockLength <= maxLength) {
                truncatedBlocks.push(block);
                totalLength += blockLength;
            }
            else {
                const remainingLength = maxLength - totalLength;
                const exceedsBuffer = blockLength - remainingLength > 50;
                // Special handling for last block or small overflows
                // want to allow small overflows to avoid awkward truncation
                if (!hasNextBlock && !exceedsBuffer) {
                    truncatedBlocks.push(block);
                    totalLength += blockLength;
                    if (totalLength > maxLength) {
                        isTruncated = false;
                    }
                    break;
                }
                // Truncate block content word by word
                // prevent words from being cut off
                if (remainingLength > 0) {
                    const truncatedContent = [];
                    let currentBlockLength = 0;
                    for (let blockContentIndex = 0; blockContentIndex < block.content.length; blockContentIndex++) {
                        const item = block.content[blockContentIndex];
                        const nextItem = block.content[blockContentIndex + 1];
                        if (item.type === 'text') {
                            const text = item.text || '';
                            const availableLength = remainingLength - currentBlockLength;
                            if (currentBlockLength + text.length > remainingLength) {
                                const words = text.split(' ');
                                let truncatedText = '';
                                let wordLength = 0;
                                for (const word of words) {
                                    if (wordLength + word.length + 1 > availableLength) {
                                        break;
                                    }
                                    truncatedText += (truncatedText ? ' ' : '') + word;
                                    wordLength += word.length + 1;
                                }
                                truncatedContent.push({
                                    ...item,
                                    text: truncatedText + '...',
                                });
                                isTruncated = true;
                                break;
                            }
                            else {
                                truncatedContent.push(item);
                                currentBlockLength += text.length;
                            }
                        }
                        else if (item.type === 'link' || item.type === 'mention') {
                            const linkLength = calculateTextLength(item.content || []);
                            // Add 1 to linkLength to account for spacing after link
                            // to prevent spacing collapse when truncating
                            const effectiveLength = linkLength + (nextItem ? 1 : 0);
                            if (currentBlockLength + effectiveLength <= remainingLength) {
                                truncatedContent.push(item);
                                // If there's a next item, add a space text node after the link
                                if (nextItem) {
                                    truncatedContent.push({
                                        styles: {},
                                        text: ' ',
                                        type: 'text',
                                    });
                                }
                                currentBlockLength += effectiveLength;
                            }
                            else {
                                isTruncated = true;
                                break;
                            }
                        }
                    }
                    if (truncatedContent.length > 0) {
                        truncatedBlocks.push({
                            ...block,
                            content: truncatedContent,
                        });
                    }
                }
                isTruncated = true;
                break;
            }
        }
        else if (totalLength < maxLength) {
            truncatedBlocks.push(block);
        }
        else {
            isTruncated = true;
            break;
        }
    }
    return { blocks: truncatedBlocks, isTruncated };
};
const schema = createSchema(DEFAULT_EDITOR_OPTIONS);
const transformNumberedListItems = (blocks) => {
    let index = 0;
    return blocks.map((block) => {
        if (block.type === 'numberedListItem') {
            index++;
            return {
                ...block,
                props: {
                    ...block.props,
                    index,
                },
            };
        }
        index = 0;
        return block;
    });
};
const renderTextContentItem = (item) => {
    let styledText = item.text;
    if (item.styles.bold) {
        styledText = _jsx("strong", { children: styledText });
    }
    if (item.styles.italic) {
        styledText = _jsx("em", { children: styledText });
    }
    if (item.styles.underline) {
        styledText = _jsx("u", { children: styledText });
    }
    if (item.styles.strike) {
        styledText = _jsx("s", { children: styledText });
    }
    if (item.styles.code) {
        styledText = _jsx("code", { children: styledText });
    }
    return styledText;
};
const renderInlineContent = (content) => {
    if (!content.length) {
        return null;
    }
    return (_jsx("div", { className: "bn-inline-content", children: content.map((item, index) => {
            if (item.type === 'link') {
                return (_jsx("a", { href: item.href, onClick: (event) => event.stopPropagation(), rel: "noreferrer", target: "_blank", children: item.content.map((contentItem) => {
                        if (contentItem.type === 'text') {
                            return renderTextContentItem(contentItem);
                        }
                        return '';
                    }) }, index));
            }
            if (item.type === 'mention') {
                return (_jsxs("a", { href: `https://contra.com/community/profile/${item.props.username}`, onClick: (event) => event.stopPropagation(), rel: "noreferrer", target: "_blank", children: ["@", item.props.name] }, index));
            }
            return renderTextContentItem(item);
        }) }));
};
const renderFunctionByType = {
    audio: Audio.render,
    blockquote: Blockquote.render,
    bulletListItem: (props) => {
        if (!props.readOnly) {
            return null;
        }
        return props.content;
    },
    checkListItem: (props) => {
        if (!props.readOnly) {
            return null;
        }
        return (_jsxs("div", { children: [_jsx("div", { contentEditable: "false", children: _jsx("input", { checked: props.block.props.checked, style: { pointerEvents: 'none' }, type: "checkbox" }) }), props.content] }));
    },
    codeblock: CodeBlock.render,
    divider: Divider.render,
    embed: Embed.render,
    file: () => null,
    heading: (props) => {
        if (!props.readOnly) {
            return null;
        }
        if (props.block.props.level === 1) {
            return _jsx("h1", { children: props.content });
        }
        if (props.block.props.level === 2) {
            return _jsx("h2", { children: props.content });
        }
        return _jsx("h3", { children: props.content });
    },
    image: Image.render,
    lottie: LottieFile.render,
    numberedListItem: (props) => {
        if (!props.readOnly) {
            return null;
        }
        return props.content;
    },
    paragraph: (props) => {
        if (!props.readOnly) {
            return null;
        }
        return props.content;
    },
    table: () => null,
    toc: TableOfContents.render,
    video: Video.render,
};
const renderBlock = (block, document, isExpanded) => {
    const type = block.type;
    if (!type) {
        return null;
    }
    const render = renderFunctionByType[type];
    return (_jsx("div", { className: "bn-block-outer", "data-id": block.id, "data-node-type": "blockOuter", children: _jsxs("div", { className: "bn-block", "data-id": block.id, "data-node-type": "blockContainer", children: [_jsx("div", { className: "bn-block-content", "data-content-type": block.type, ...Object.fromEntries(Object.entries(block.props ?? {})
                        .filter(([property, value]) => !inheritedProps.includes(property) &&
                        value !==
                            schema.blockSchema[type].propSchema[property]?.default)
                        .map(([property, value]) => {
                        return [camelToDataKebab(property), value];
                    })), children: render({
                        block,
                        content: block.content
                            ? renderInlineContent(block.content)
                            : null,
                        document,
                        readOnly: true,
                    }) }), block.children?.length ? (_jsx("div", { className: "bn-block-group", "data-node-type": "blockGroup", children: transformNumberedListItems(block.children).map((child) => {
                        return renderBlock(child, document, isExpanded);
                    }) })) : null] }) }, block.id));
};
export const renderReadOnlyContent = ({ document, fetchWebPageMetadata, maxLength, }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { blocks, isTruncated } = isExpanded
        ? { blocks: document, isTruncated: false }
        : maybeTruncateContent(document, maxLength);
    return (_jsx(ContraEditorContextProvider, { value: {
            fetchWebPageMetadata,
        }, children: _jsx(MantineProvider, { cssVariablesSelector: ".bn-mantine", getRootElement: () => undefined, children: _jsx("div", { className: "bn-container light bn-mantine contra-bn-read-only", "data-color-scheme": "light", children: _jsxs("div", { className: "ProseMirror bn-editor bn-default-styles ProseMirror-focused", children: [_jsx("div", { className: "bn-block-group", "data-node-type": "blockGroup", children: transformNumberedListItems(blocks).map((block) => {
                                return renderBlock(block, document, isExpanded);
                            }) }), typeof maxLength === 'number' && isTruncated ? (_jsx("button", { className: "contra-show-more-button", onClick: (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setIsExpanded((previous) => !previous);
                            }, type: "button", children: "Show more" })) : null] }) }) }) }));
};
//# sourceMappingURL=renderReadOnlyContent.js.map